import { LOG } from 'src/app/common/util/log';

import { ControlInstructionParser } from '@utils/control-instruction-parser';
import { DefaultActionsProxy, getUniversalActionProxy } from '../actions';
import { ActionFn, ActionFunctions, CellContent, CellContentObject, Table, TableConstructionData, TableRow, TableSorter } from '../types';
import {
  TableControlInstructionCallContext,
  TableControlInstructionFunctions
} from '../types/table-control-instructions';
import { isInstanceOfGroupingDefsCell } from '../util';
import { TableLayoutHandler } from './table-layout-handler';
import { TableMetadataHandler } from './table-metadata-handler';
import { TableRowSortHandler } from './table-row-sort-handler';



export class TableConstructor {
  private groupColumnIndex?: number = undefined;

  private readonly actionFunctions: ActionFunctions;

  private readonly layoutHandler: TableLayoutHandler;
  private readonly rowSortHandler: TableRowSortHandler;
  private readonly metadataHandler: TableMetadataHandler;

  private readonly instructionParser: ControlInstructionParser<TableControlInstructionFunctions, TableControlInstructionCallContext>;

  constructor();
  constructor(actionHandler: ActionFn | ActionFunctions);
  constructor(
    actionHandler: ActionFn | ActionFunctions,
    tableLayoutHandler: TableLayoutHandler,
    tableRowSortHandler: TableRowSortHandler,
    tableMetadataHandler: TableMetadataHandler
  );
  constructor(
    actionHandler?: ActionFn | ActionFunctions,
    tableLayoutHandler?: TableLayoutHandler,
    tableRowSortHandler?: TableRowSortHandler,
    tableMetadataHandler?: TableMetadataHandler
  ) {
    this.layoutHandler = tableLayoutHandler ?? new TableLayoutHandler();
    this.rowSortHandler = tableRowSortHandler ?? new TableRowSortHandler();
    this.metadataHandler = tableMetadataHandler ?? new TableMetadataHandler();

    if (!actionHandler) {
      this.actionFunctions = DefaultActionsProxy;
    } else if (typeof actionHandler === 'function') {
      this.actionFunctions = getUniversalActionProxy(actionHandler);
    } else {
      this.actionFunctions = actionHandler;
    }

    this.instructionParser = new ControlInstructionParser(this.getMappedControlInstructionFunctions());
    this.instructionParser.logContextLabel = this.logContextLabel;
  }

  public buildTable(constructionData: TableConstructionData): [Table, TableSorter] {
    const rawHeaders = constructionData.headers.map(cell => cell?.toString() ?? '');
    const controlInstructions = constructionData.controlInstructions;
    const rows = constructionData.rows.map(row => <TableRow>{ content: row, seperate: false });

    this.parseControlInstructions(controlInstructions);

    const headers = this.layoutHandler.buildTableColumnDefinitions(rawHeaders);
    const metadata = this.metadataHandler.metadata;

    this.setRowMetadata(rows);

    const constructedTable: Table = {
      headers: headers,
      meta: metadata,
      rows: rows,
    };

    const tableSorter = this.rowSortHandler.buildTableSorter(constructedTable, this.metadataHandler);

    return [constructedTable, tableSorter];
  }

  private logContextLabel(context: TableControlInstructionCallContext): string {
      return `Column ${context.columnIndex}`;
  }

  private parseControlInstructions(cells: string[]): void {
    cells.forEach((value, columnIndex) => {
      if (!value) {
        return;
      }

      this.instructionParser.parseControlInstructions(value, { columnIndex: columnIndex });
    });
  }

  private setRowMetadata(rows: TableRow[]): void {
    if (this.groupColumnIndex !== undefined) {
      const columnIndex = this.groupColumnIndex;
      for (const row of rows) {
        const groupDef: CellContent = row.content[columnIndex];
        if (isInstanceOfGroupingDefsCell(groupDef) && groupDef.isHeader) {
          row.meta = { isGroupHeader: true };
        }
      }
    }
  }

  private readonly ciColumn = (context: TableControlInstructionCallContext, groupId?: string): void => {
    if (!groupId) {
      LOG.error(context.instructionName, '- Invalid group:', groupId);
      return;
    }

    this.layoutHandler.addToColumnGroup(context.columnIndex, groupId);
  };

  private readonly ciAlerter = (
    context: TableControlInstructionCallContext,
    greenOperator?: string,
    neutralValue?: string,
    redOperator?: string
  ): void => {
    if (!greenOperator || !neutralValue || !redOperator) {
      LOG.error(context.instructionName, '- Invalid alerter:', greenOperator, neutralValue, redOperator);
      return;
    }

    this.metadataHandler.addAlerter(context.columnIndex, greenOperator, redOperator, neutralValue);
  };

  private readonly ciCellStyle = (context: TableControlInstructionCallContext, ...styles: string[]): void => {
    if (styles) {
      styles = styles.filter(Boolean);
    }

    if (!styles || styles.length < 1) {
      LOG.warn(context.instructionName, '- No styles found.');
      return;
    }

    this.metadataHandler.addCellStyle(context.columnIndex, styles);
  };

  private readonly ciAction = (context: TableControlInstructionCallContext, actionName?: string): void => {
    if (!actionName) {
      LOG.error(this.actionFunctions, '- No function passed.');
      return;
    }

    const action = this.actionFunctions[actionName];

    if (!action) {
      LOG.error(context.instructionName, '- Unknown action function:', actionName);
      return;
    }

    this.metadataHandler.addAction(context.columnIndex, action);
  };

  private readonly ciHidden = (context: TableControlInstructionCallContext): void => {
    this.layoutHandler.hideColumn(context.columnIndex);
  };

  private readonly ciAlign = (context: TableControlInstructionCallContext, alignment?: string): void => {
    if (!alignment) {
      LOG.error(context, '- No alignment passed.');
      return;
    }

    this.metadataHandler.addAlign(context.columnIndex, alignment);
  };

  private readonly ciGroups = (context: TableControlInstructionCallContext): void => {
    this.rowSortHandler.setAsPrimaryColumn(context.columnIndex);
    this.groupColumnIndex = context.columnIndex;
  };

  private readonly ciHeaderColspan = (context: TableControlInstructionCallContext, span?: string): void => {
    if (!span) {
      LOG.error(context, '- No colspan passed.');
      return;
    }

    this.layoutHandler.setHeaderColspan(context.columnIndex, span);
  };

  private readonly ciUnsortable = (context: TableControlInstructionCallContext): void => {
    this.metadataHandler.addUnsortable(context.columnIndex);
  };

  private readonly ciSortBy = (context: TableControlInstructionCallContext, option?: string): void => {
    if (option && (option !== 'label' && option !== 'value')) {
      LOG.error(context, '- Invalid sortBy option. Possible values are \'value\' and \'label\'');
      return;
    }

    this.metadataHandler.addSortBy(context.columnIndex, option as keyof CellContentObject);
  }

  private readonly ciExcludeGroupHeadersFromActions = (context: TableControlInstructionCallContext): void => {
    this.metadataHandler.actionsExcludeGroupHeader(context.columnIndex);
  }

  private getMappedControlInstructionFunctions(): TableControlInstructionFunctions {
    return {
      column: this.ciColumn,

      groups: this.ciGroups,
      group: this.ciGroups,

      headerColspan: this.ciHeaderColspan,

      alerter: this.ciAlerter,

      cellStyle: this.ciCellStyle,

      action: this.ciAction,
      excludeGroupHeadersFromActions: this.ciExcludeGroupHeadersFromActions,

      hidden: this.ciHidden,

      align: this.ciAlign,

      sortBy: this.ciSortBy,
      unsortable: this.ciUnsortable,
    };
  }
}
